import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestService } from './rest.service';
import { ColorType, GetJourneyDto, Journey } from '@interfaces/rest/interfaces/journey';
import { ProvinceService } from './province.service';
import { License } from '@interfaces/rest/interfaces/licenses';
import { User } from '@interfaces/user';

@Injectable({
  providedIn: 'root'
})
export class JourneyService {
  journeys: Journey[] = [];
  licenses: License[] = [];

  // Opción 4: Observable para arquitectura reactiva
  private journeysSubject = new BehaviorSubject<Journey[]>([]);
  public journeys$ = this.journeysSubject.asObservable();

  // 🔥 Nuevo observable para todos los servicios (sin eliminar)
  allJourneys: Journey[] = [];
  private allJourneysSubject = new BehaviorSubject<Journey[]>([]);
  public allJourneys$ = this.allJourneysSubject.asObservable();

  constructor(private rest: RestService,
    private provinceService: ProvinceService) {
  }

  // Opción 3: Getter para acceder a los journeys
  getJourneys(): Journey[] {
    return this.journeys;
  }

  // Opción 4: Observable
  getJourneys$(): Observable<Journey[]> {
    return this.journeys$;
  }

  async getInicialJourneys(provinceId: number): Promise<void> {
    this.licenses = await this.provinceService.getLicenses();
    let searchParams: GetJourneyDto = {
      limit: 200,
      'where.province': provinceId,
      'where.status': ['PENDING', 'DRIVER_FOUND', 'DRIVER_SEARCHING', 'NEW']
    };

    const resp = await this.rest.getJourneysAll(searchParams);
    if (resp.ok) {
      resp.response.data.result.forEach((journey: Journey) => {
        this.createJourneyFields(journey);
      });
      this.journeys = resp.response.data.result;
      // Actualizar el observable
      console.log('this.journeys', this.journeys);
      this.sendJourneys();
    }
  }

  async createJourneyFields(journey: Journey) {
    if (journey.fromAddress) {
      journey.from = `${journey.fromAddress?.title || ''} - ${journey.fromAddress?.description || ''}`;
    }
    if (journey.toAddress) {
      journey.to = `${journey.toAddress?.title || ''} - ${journey.toAddress?.description || ''}`;
    }
    let color: ColorType = 'white';
    try {
      // Extraer el licenseId - puede venir como objeto con .id o directamente como id
      let licenseId: number | null = null;
      if (journey.license) {
        if (typeof journey.license === 'object' && 'id' in journey.license) {
          // Es un objeto con la propiedad id
          licenseId = journey.license.id;
        } else if (typeof journey.license === 'number') {
          // Es un id directo
          licenseId = journey.license as any;
        }
      }

      // Extraer el driverId - puede venir como objeto con .id o directamente como id
      let driverId: number | null = null;
      if (journey.driver) {
        if (typeof journey.driver === 'object' && 'id' in journey.driver) {
          // Es un objeto con la propiedad id
          driverId = journey.driver.id ?? null;
        } else if (typeof journey.driver === 'number') {
          // Es un id directo
          driverId = journey.driver as any;
        }
      }

      // Si no hay taxiNumber y tenemos un licenseId, buscar la licencia completa
      let license: License | null = null;
      if (licenseId && (!journey.license || typeof journey.license !== 'object' || !(journey.license as License).taxiNumber)) {
        license = await this.getLicense(licenseId);
        if (license) {
          journey.license = license;
        }
      }

      // Si no hay name y tenemos driverId, buscar el driver completo
      let driver: User | null = null;
      if (driverId && (!journey.driver || typeof journey.driver !== 'object' || !(journey.driver as User).name)) {
        // Primero intentar buscar con la licencia si está disponible
        if (journey.license && typeof journey.license === 'object') {
          driver = await this.getDriver(journey.license as License, driverId);
          if (driver) {
            journey.driver = driver;
          }
        }
        // Si no se encontró, buscar por id en todas las licencias
        if (!driver || !driver.name) {
          driver = this.getDriverById(driverId);
          if (driver) {
            journey.driver = driver;
          }
        }
      }

      if (!journey.license?.taxiNumber && journey.driver?.email) {
        journey.license = {
          taxiNumber: this.getPosibleTaxiNumber(journey.driver?.email)
        } as any;
      }

      if (!journey.phone && journey.user?.phone) {
        journey.phone = journey.user?.phone;
      }
    } catch (error) {
      console.error('error', error);
    }

    console.log('journey.driver?.name', journey.driver?.name);
    console.log('journey.license?.taxiNumber', journey.license?.taxiNumber);
    console.log('journey.nameLic', `${journey.driver?.name || ''}${journey.license?.taxiNumber ? ('(' + journey.license?.taxiNumber + ')') : ''}`);

    journey.nameLic = `${journey.driver?.name || ''}${journey.license?.taxiNumber ? ('(' + journey.license?.taxiNumber + ')') : ''}`;
    if (journey.status) {
      if (['PENDING'].includes(journey.status)) {
        color = 'red';
      } else if (['DRIVER_FOUND'].includes(journey.status)) {
        color = 'green';
      } else if (['DRIVER_SEARCHING', 'NEW'].includes(journey.status)) {
        color = 'blue';
      }
      if (!journey.statusObj) {
        journey.statusObj = [];
      }
      journey.statusObj.push({
        name: journey.status,
        translatePath: 'services',
        color
      });
    }
    return journey;
  }

  async getLicense(licenseId: number) {
    if (!this.licenses.length) {
      this.licenses = await this.provinceService.getLicenses();
    }
    const license = this.licenses.find(elem => elem.id === licenseId);
    if (license) {
      return license;
    }
    return null;
  }

  getPosibleTaxiNumber(email: string) {
    // emailFormat = 'licencia73-calvia@mallorcab.com';
    if (!email) return '';

    // Buscar patrón donde 'licencia' va seguido de números y un guión
    const match = email.match(/licencia(\d+)-/i);
    if (match && match[1]) {
      return match[1]; // Devolver solo los números encontrados
    }

    // Si no encuentra el patrón, intentar split
    const parts = email.split('licencia');
    if (parts.length > 1) {
      const numberPart = parts[1].split('-')[0];
      if (numberPart && !isNaN(Number(numberPart))) {
        return numberPart;
      }
    }

    return '';
  }

  async getDriver(license: License, driverId: number) {
    const driver = license.drivers?.find(elem => elem.id === driverId);
    if (driver) {
      return driver;
    }
    return null;
  }

  getDriverById(driverId: number): User | null {
    for (const license of this.licenses) {
      const driver = license.drivers?.find(elem => elem.id === driverId);
      if (driver) {
        return driver;
      }
    }
    return null;
  }

  sendJourneys() {
    // Sort journeys before sending them
    this.journeys = this.journeys.sort((a, b) => {
      // First sort by large flag
      if (a.large !== b.large) {
        return b.large ? 1 : -1;
      }

      // If same large status, sort by createdAt within large group
      if (a.large && b.large) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }

      // Then sort by priority
      if (a.priority !== b.priority) {
        return b.priority ? 1 : -1;
      }

      // If same priority, sort by createdAt
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    this.journeysSubject.next(this.journeys);
  }

  async setNewInfo(journey: Journey) {
    if (!this.licenses.length) {
      this.licenses = await this.provinceService.getLicenses();
    }
    this.createJourneyFields(journey);
    const index = this.journeys.findIndex(j => j.id === journey.id);

    journey.fadeInOut = true;
    setTimeout(() => {
      journey.fadeInOut = false;
    }, 2000);

    if (index === -1) {
      if (journey.status && ['PENDING', 'DRIVER_FOUND', 'DRIVER_SEARCHING', 'NEW'].includes(journey.status)) {
        this.journeys.push(journey);
      }
    } else {
      if (journey.status && !['PENDING', 'DRIVER_FOUND', 'DRIVER_SEARCHING', 'NEW'].includes(journey.status)) {
        journey.fadeInOut = false;
        journey.toRemove = true;
        setTimeout(() => {
          this.journeys = this.journeys.filter(j => j.id !== journey.id);
          this.sendJourneys();
        }, 4500);
      }
      this.journeys[index] = { ...this.journeys[index], ...journey };
    }
    this.sendJourneys();
  }

  /**
   * 🔥 Obtiene todos los servicios iniciales (últimos 100)
   */
  async getAllJourneysInitial(provinceId: number): Promise<void> {
    if (!this.licenses.length) {
      this.licenses = await this.provinceService.getLicenses();
    }
    let searchParams: GetJourneyDto = {
      limit: 1000,
      'where.province': provinceId,
      'sort_by': 'id:desc'
    };

    const resp = await this.rest.getJourneysAll(searchParams);
    if (resp.ok) {
      resp.response.data.result.forEach((journey: Journey) => {
        this.createJourneyFields(journey);
      });
      this.allJourneys = resp.response.data.result;
      this.sendAllJourneys();
    }
  }

  /**
   * 🔥 Actualiza o agrega un servicio a la lista de todos los servicios (sin eliminar)
   * Este método se usa para actualizar servicios cuando llegan updates parciales
   */
  async setAllJourneysInfo(journey: Journey) {
    if (!this.licenses.length) {
      this.licenses = await this.provinceService.getLicenses();
    }
    await this.createJourneyFields(journey);
    const index = this.allJourneys.findIndex(j => j.id === journey.id);

    journey.fadeInOut = true;
    setTimeout(() => {
      journey.fadeInOut = false;
    }, 2000);

    if (index === -1) {
      // Si no existe, agregarlo
      this.allJourneys.push(journey);
    } else {
      // Si existe, actualizar manteniendo propiedades existentes
      this.allJourneys[index] = { ...this.allJourneys[index], ...journey };
    }
    this.sendAllJourneys();
  }

  /**
   * 🔥 Envía todos los servicios a los suscritos
   */
  sendAllJourneys() {
    // Ordenar por fecha de creación descendente
    this.allJourneys = this.allJourneys.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    this.allJourneysSubject.next(this.allJourneys);
  }

  /**
   * 🔥 Obtiene el observable de todos los servicios
   */
  getAllJourneys$(): Observable<Journey[]> {
    return this.allJourneys$;
  }

  /**
   * 🔥 Agrega múltiples servicios (útil después de búsqueda en BD)
   */
  addJourneysToAll(journeys: Journey[]) {
    journeys.forEach(journey => {
      const index = this.allJourneys.findIndex(j => j.id === journey.id);
      if (index === -1) {
        this.createJourneyFields(journey);
        this.allJourneys.push(journey);
      } else {
        this.createJourneyFields(journey);
        this.allJourneys[index] = { ...this.allJourneys[index], ...journey };
      }
    });
    this.sendAllJourneys();
  }

  /**
   * 🔥 Sincroniza los últimos 100 servicios con los existentes
   * Obtiene los últimos 100, hace match por ID y actualiza/sustituye los existentes
   * Los nuevos se añaden en el orden correcto (ordenados por ID descendente)
   */
  async syncLatestJourneys(provinceId: number): Promise<void> {
    if (!this.licenses.length) {
      this.licenses = await this.provinceService.getLicenses();
    }

    const searchParams: GetJourneyDto = {
      limit: 100,
      'where.province': provinceId,
      'sort_by': 'id:desc'
    };

    const resp = await this.rest.getJourneysAll(searchParams);
    if (resp.ok && resp.response.data.result) {
      const latestJourneys = resp.response.data.result;

      // Crear un mapa de los servicios existentes por ID para búsqueda rápida
      const existingJourneysMap = new Map<number, Journey>();
      this.allJourneys.forEach(journey => {
        if (journey.id) {
          existingJourneysMap.set(journey.id, journey);
        }
      });

      // Procesar cada servicio de los últimos 100
      latestJourneys.forEach((journey: Journey) => {
        // Procesar campos del journey
        this.createJourneyFields(journey);

        const existingIndex = this.allJourneys.findIndex(j => j.id === journey.id);

        if (existingIndex === -1) {
          // Si no existe, agregarlo
          this.allJourneys.push(journey);
        } else {
          // Si existe, sustituirlo completamente con los datos actualizados
          this.allJourneys[existingIndex] = journey;
        }
      });

      // Ordenar todos los servicios por ID descendente (más recientes primero)
      this.allJourneys = this.allJourneys.sort((a, b) => {
        const aId = a.id || 0;
        const bId = b.id || 0;
        return bId - aId;
      });

      // Enviar los servicios actualizados
      this.sendAllJourneys();

      console.log('✅ Sincronización completada:', {
        totalServicios: this.allJourneys.length,
        nuevosServicios: latestJourneys.length
      });
    }
  }

}
